import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiChevronLeft } from 'react-icons/hi2';
import { getStationShows, getRecentShowEpisodes } from '../api/client';
import type { Stream, StationShow, RecentEpisode } from '../types';

interface ShowsPanelProps {
  stream: Stream;
}

// Format relative date
function formatDate(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;

  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

export function ShowsPanel({ stream }: ShowsPanelProps) {
  const navigate = useNavigate();

  const [shows, setShows] = useState<StationShow[]>([]);
  const [loading, setLoading] = useState(true);

  // Selected show + episodes
  const [selectedShow, setSelectedShow] = useState<StationShow | null>(null);
  const [episodes, setEpisodes] = useState<RecentEpisode[]>([]);
  const [episodesLoading, setEpisodesLoading] = useState(false);
  const [episodeLimit, setEpisodeLimit] = useState(20);
  const [hasMoreEpisodes, setHasMoreEpisodes] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    async function loadShows() {
      try {
        const stationShows = await getStationShows(stream.streamid);
        setShows(stationShows);
      } catch {
        // silently fail - empty grid
      } finally {
        setLoading(false);
      }
    }

    loadShows();
  }, [stream.streamid]);

  const handleShowClick = async (show: StationShow) => {
    setSelectedShow(show);
    setEpisodesLoading(true);
    setEpisodes([]);
    setEpisodeLimit(20);
    setHasMoreEpisodes(false);
    try {
      const result = await getRecentShowEpisodes(show.showid, 20);
      setEpisodes(result);
      setHasMoreEpisodes(result.length >= 20);
    } catch {
      // empty list on error
    } finally {
      setEpisodesLoading(false);
    }
  };

  const handleLoadMore = async () => {
    if (!selectedShow || loadingMore) return;
    setLoadingMore(true);
    const newLimit = episodeLimit + 20;
    try {
      const result = await getRecentShowEpisodes(selectedShow.showid, newLimit);
      setEpisodes(result);
      setEpisodeLimit(newLimit);
      setHasMoreEpisodes(result.length >= newLimit);
    } catch {
      // keep existing
    } finally {
      setLoadingMore(false);
    }
  };

  const handleBack = () => {
    setSelectedShow(null);
    setEpisodes([]);
  };

  const handlePlayEpisode = (episodeId: number) => {
    navigate(`/${episodeId}`);
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div
            className="inline-block w-8 h-8 border-2 border-[var(--color-border)]
                        border-t-[var(--color-primary)] rounded-full animate-spin"
          />
          <p className="mt-2 text-xs text-[var(--color-text-muted)]">Loading shows...</p>
        </div>
      </div>
    );
  }

  if (shows.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-sm text-[var(--color-text-muted)]">No shows available</p>
      </div>
    );
  }

  // Episode list view for selected show
  if (selectedShow) {
    return (
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header with back button and show info */}
        <div className="flex-shrink-0 px-4 py-3 border-b border-[var(--color-border)]/30
                       bg-[var(--color-surface)]/30 backdrop-blur-md">
          <div className="max-w-3xl mx-auto flex items-center gap-3">
            <button
              onClick={handleBack}
              className="flex items-center gap-1 text-xs font-medium
                        text-[var(--color-text-muted)] hover:text-[var(--color-text)]
                        transition-colors cursor-pointer flex-shrink-0"
            >
              <HiChevronLeft size={16} />
              Shows
            </button>
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <img
                src={selectedShow.image || stream.image}
                alt=""
                className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  if (target.src !== stream.image) target.src = stream.image;
                }}
              />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[var(--color-text)] truncate">
                  {selectedShow.title}
                </p>
                {selectedShow.presenter && (
                  <p className="text-xs text-[var(--color-text-muted)] truncate">
                    {selectedShow.presenter}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Episode list */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto px-4 py-3">
            {episodesLoading ? (
              <div className="flex items-center justify-center py-12">
                <div
                  className="inline-block w-6 h-6 border-2 border-[var(--color-border)]
                              border-t-[var(--color-primary)] rounded-full animate-spin"
                />
              </div>
            ) : episodes.length === 0 ? (
              <p className="text-sm text-[var(--color-text-muted)] text-center py-12">
                No episodes available
              </p>
            ) : (
              <div className="space-y-1">
                {episodes.map((ep) => (
                  <button
                    key={ep.episodeid}
                    onClick={() => handlePlayEpisode(ep.episodeid)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
                              bg-[var(--color-surface)]/20 hover:bg-[var(--color-surface)]/50
                              border border-transparent hover:border-[var(--color-border)]/30
                              transition-all duration-150 group text-left cursor-pointer
                              active:scale-[0.99]"
                  >
                    <img
                      src={ep.image || selectedShow.image || stream.image}
                      alt=""
                      className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        if (target.src !== stream.image) target.src = stream.image;
                      }}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-[var(--color-text)] line-clamp-2
                                   group-hover:text-[var(--color-primary)] transition-colors">
                        {ep.episodetitle}
                      </p>
                      <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
                        {formatDate(ep.datetime)}
                      </p>
                    </div>
                  </button>
                ))}
                {hasMoreEpisodes && (
                  <button
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="w-full py-2.5 text-xs font-medium text-[var(--color-primary)]
                              hover:text-[var(--color-text)] transition-colors cursor-pointer
                              disabled:opacity-50 disabled:cursor-wait"
                  >
                    {loadingMore ? 'Loading...' : 'Load more episodes'}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Shows grid view
  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {shows.map((show) => (
            <button
              key={show.showid}
              onClick={() => handleShowClick(show)}
              className="flex flex-col rounded-xl overflow-hidden
                        bg-[var(--color-surface)]/40 backdrop-blur-md
                        hover:bg-[var(--color-surface)]/70
                        border border-[var(--color-border)]/20 hover:border-[var(--color-border)]/50
                        transition-all duration-200 group text-left
                        cursor-pointer hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]"
            >
              {/* Show Image */}
              <div className="relative aspect-square w-full overflow-hidden bg-[var(--color-surface)]/30">
                <img
                  src={show.image || stream.image}
                  alt=""
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    if (target.src !== stream.image) {
                      target.src = stream.image;
                    }
                  }}
                />
              </div>

              {/* Show Info */}
              <div className="p-3">
                <p className="text-sm font-medium text-[var(--color-text)] line-clamp-2
                             group-hover:text-[var(--color-primary)] transition-colors">
                  {show.title}
                </p>
                {show.presenter && (
                  <p className="text-xs text-[var(--color-text-muted)] mt-1 truncate">
                    {show.presenter}
                  </p>
                )}
                {show.description && (
                  <p className="text-xs text-[var(--color-text-muted)] mt-1 line-clamp-2">
                    {show.description}
                  </p>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
